/**
 * Per-tenant × permission grid (D-09, D-10).
 *
 * Rows are tenants, columns are permission codes. Each cell is a checkbox.
 * The parent owns the grid state; this component is purely controlled.
 *
 * Default behaviour for the create form (per Max): every cell starts
 * checked. The detail form pre-fills from the operator's current grant
 * set. Either form drops zero-checked tenant rows from the submitted
 * payload via `toInitialPermissions`.
 */

export interface PermissionGridTenant {
  tenantCod: string;
  tenantLib: string;
}

export interface PermissionGridPermission {
  code: string;
  description: string | null;
}

/** Per-tenant set of granted permission codes. Arrays (not Sets) for serialisability. */
export type PermissionGridValue = Record<string, string[]>;

export interface PermissionGridProps {
  tenants: PermissionGridTenant[];
  permissions: PermissionGridPermission[];
  value: PermissionGridValue;
  onChange: (next: PermissionGridValue) => void;
  disabled?: boolean;
  /**
   * Ce que l'attributeur peut donner, par tenant (#149) : on ne donne que ce
   * que l'on detient soi-meme sur ce tenant. Une case hors de cette liste
   * est grisee ; un tenant absent de la carte est entierement grise. Sans
   * cette prop, tout est permis — c'est le comportement d'avant, garde pour
   * les usages sans attributeur.
   */
  grantable?: Record<string, readonly string[]>;
}

/** La case est-elle a la main de l'attributeur ? */
export function canGrant(
  grantable: PermissionGridProps['grantable'],
  tenantCod: string,
  code: string,
): boolean {
  if (!grantable) return true;
  return (grantable[tenantCod] ?? []).includes(code);
}

// ─── Pure state helpers ────────────────────────────────────────────────────

/** Initialise a grid with every cell checked across every tenant. */
export function allCheckedGrid(
  tenants: PermissionGridTenant[],
  permissions: PermissionGridPermission[],
): PermissionGridValue {
  return Object.fromEntries(tenants.map((t) => [t.tenantCod, permissions.map((p) => p.code)]));
}

/** Initialise a grid where every cell is unchecked. */
export function emptyGrid(tenants: PermissionGridTenant[]): PermissionGridValue {
  return Object.fromEntries(tenants.map((t) => [t.tenantCod, [] as string[]]));
}

/** Toggle a single cell on/off, returning a new grid. */
export function toggleCell(
  grid: PermissionGridValue,
  tenantCod: string,
  code: string,
): PermissionGridValue {
  const current = grid[tenantCod] ?? [];
  const has = current.includes(code);
  const next = has ? current.filter((c) => c !== code) : [...current, code];
  return { ...grid, [tenantCod]: next };
}

/** Replace a tenant's row wholesale (used by the per-row "Select all" toggle). */
export function setRow(
  grid: PermissionGridValue,
  tenantCod: string,
  codes: string[],
): PermissionGridValue {
  return { ...grid, [tenantCod]: codes };
}

/**
 * Build the backend payload from the grid state. Drops tenant rows with
 * zero granted permissions so the operator doesn't get an empty-tenant
 * grant row in user_permission.
 */
export function toInitialPermissions(
  grid: PermissionGridValue,
): Array<{ tenantCod: string; permissionCodes: string[] }> {
  return Object.entries(grid)
    .filter(([, codes]) => codes.length > 0)
    .map(([tenantCod, codes]) => ({
      tenantCod,
      permissionCodes: [...codes].sort(),
    }));
}

// ─── Component ─────────────────────────────────────────────────────────────

export function PermissionGrid({
  tenants,
  permissions,
  value,
  onChange,
  disabled = false,
  grantable,
}: PermissionGridProps) {
  if (tenants.length === 0 || permissions.length === 0) {
    return (
      <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-6 text-center text-sm text-[#999]">
        No tenants or permissions to display.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-[#e0e0e0] bg-white">
      <table className="w-full text-[13px]">
        <thead className="bg-[#fafafa]">
          <tr>
            <th className="sticky left-0 z-10 border-b border-[#e8e8e8] bg-[#fafafa] px-4 py-2 text-left font-semibold text-[#333]">
              Tenant
            </th>
            {permissions.map((p) => (
              <th
                key={p.code}
                className="border-b border-[#e8e8e8] px-3 py-2 text-center font-medium text-[#444]"
                title={p.description ?? undefined}
              >
                {p.code}
              </th>
            ))}
            <th className="border-b border-[#e8e8e8] px-3 py-2 text-center font-medium text-[#444]">
              All
            </th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => {
            const row = value[t.tenantCod] ?? [];
            // « Tout » ne porte que sur ce que l'attributeur peut donner : les
            // cases hors de sa main gardent leur etat, cochees ou non.
            const grantableHere = permissions.filter((p) =>
              canGrant(grantable, t.tenantCod, p.code),
            );
            const allChecked =
              grantableHere.length > 0 && grantableHere.every((p) => row.includes(p.code));
            const noneChecked = row.length === 0;
            const rowLocked = disabled || grantableHere.length === 0;
            return (
              <tr key={t.tenantCod} className="even:bg-[#fcfcfc]">
                <td className="sticky left-0 z-10 bg-inherit px-4 py-2 font-semibold text-[#333]">
                  {t.tenantLib}{' '}
                  <span className="text-[11px] font-normal text-[#888]">({t.tenantCod})</span>
                </td>
                {permissions.map((p) => {
                  const checked = row.includes(p.code);
                  const allowed = canGrant(grantable, t.tenantCod, p.code);
                  return (
                    <td key={p.code} className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${t.tenantCod} · ${p.code}`}
                        checked={checked}
                        disabled={disabled || !allowed}
                        title={allowed ? undefined : `You do not hold ${p.code} on ${t.tenantCod}`}
                        onChange={() => onChange(toggleCell(value, t.tenantCod, p.code))}
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    className="text-[11px] font-semibold text-[#6B0F1A] underline disabled:opacity-50"
                    disabled={rowLocked}
                    onClick={() => {
                      const grantableCodes = grantableHere.map((p) => p.code);
                      const kept = row.filter((c) => !grantableCodes.includes(c));
                      onChange(
                        setRow(
                          value,
                          t.tenantCod,
                          allChecked ? kept : [...kept, ...grantableCodes],
                        ),
                      );
                    }}
                  >
                    {allChecked ? 'Clear' : noneChecked ? 'Select all' : 'Select all'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
